import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchJson, assetUrl as _a } from "../lib/api";
import { useDocumentMeta } from "../lib/useDocumentMeta";
import { formatDateFR } from "../lib/format";

/**
 * Static legal page renderer.
 *
 * Loads `/api/legal/:slug`. Fully typography-only, matches PlaceholderPage's
 * visual footprint so we don't disturb the design. Falls back to a soft
 * placeholder if the CMS row is missing (that's a seed issue, not a UX
 * failure).
 *
 * Content is stored as light Markdown; we render **bold**, italic, headings,
 * lists and paragraph breaks manually to avoid pulling in a full markdown
 * dep for 3 pages.
 */
export default function LegalPage({ slug, fallbackTitle, fallbackEyebrow, fallbackSubtitle }) {
  const params = useParams();
  const finalSlug = slug || params.slug;
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await fetchJson(`/legal/${finalSlug}`);
        setRow(data);
      } catch (_e) { setRow(null); }
      finally { setLoading(false); }
    })();
  }, [finalSlug]);

  useDocumentMeta({
    title: row?.title || fallbackTitle || "Page",
    description: fallbackSubtitle || "",
  });

  const title = row?.title || fallbackTitle || "";
  const eyebrow = row?.eyebrow || fallbackEyebrow || "Légal";
  const content = row?.content || "";

  return (
    <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-14 md:py-20" data-testid={`legal-page-${finalSlug}`}>
      <p className="text-xs uppercase tracking-[0.3em] text-gold font-stats">{eyebrow}</p>
      <h1
        className="mt-6 font-display font-black text-4xl sm:text-5xl md:text-7xl lg:text-8xl text-white leading-[0.85]"
        data-testid="legal-title"
      >
        {title}
      </h1>

      {loading ? (
        <p className="mt-8 text-neutral-600 font-stats text-xs uppercase tracking-widest animate-pulse">
          Chargement…
        </p>
      ) : content ? (
        <div className="mt-10 md:mt-14 max-w-3xl">
          <MarkdownLite text={content} />
          {row?.updated_at && (
            <p className="mt-10 text-[10px] font-stats uppercase tracking-widest text-neutral-600" data-testid="legal-updated-at">
              Mis à jour le {formatDateFR(row.updated_at)}
            </p>
          )}
        </div>
      ) : (
        <p className="mt-8 text-neutral-500 max-w-2xl">{fallbackSubtitle}</p>
      )}
    </section>
  );
}

// Very lightweight markdown → JSX (headings, bold, italic, links, list items,
// paragraph breaks). Enough for admin-edited legal text; not a full parser.
function MarkdownLite({ text }) {
  const blocks = String(text).split(/\n{2,}/);
  return (
    <div className="space-y-5 text-neutral-300 text-sm md:text-base leading-relaxed">
      {blocks.map((b, i) => {
        const trimmed = b.trim();
        if (!trimmed) return null;
        const stableKey = `${i}-${trimmed.slice(0, 32)}`;
        if (trimmed.startsWith("## ")) {
          return (
            <h2 key={stableKey} className="mt-6 font-display font-black text-white uppercase text-xl md:text-2xl tracking-tight">
              {trimmed.slice(3)}
            </h2>
          );
        }
        if (trimmed.startsWith("# ")) {
          return (
            <h2 key={stableKey} className="mt-6 font-display font-black text-white uppercase text-2xl md:text-3xl tracking-tight">
              {trimmed.slice(2)}
            </h2>
          );
        }
        // Bullet list block
        if (/^-\s+/.test(trimmed)) {
          const items = trimmed.split(/\n/).filter((l) => /^-\s+/.test(l)).map((l) => l.replace(/^-\s+/, ""));
          return (
            <ul key={stableKey} className="list-disc list-inside space-y-1 text-neutral-300">
              {items.map((it, j) => <li key={`${j}-${it.slice(0, 32)}`}>{inline(it)}</li>)}
            </ul>
          );
        }
        return <p key={stableKey}>{inline(trimmed)}</p>;
      })}
    </div>
  );
}

function inline(text) {
  // Escape then re-inject spans for **bold**, *italic*, [txt](url)
  const parts = [];
  let rest = text;
  const push = (v) => parts.push(v);
  // Very naive replacements — safe for admin-edited plain text.
  const bold = /\*\*([^*]+)\*\*/;
  const link = /\[([^\]]+)\]\(([^)]+)\)/;
  const italic = /\*([^*]+)\*/;
  let iter = 0;
  while (rest && iter++ < 200) {
    const lm = rest.match(link);
    const bm = rest.match(bold);
    const im = rest.match(italic);
    const candidates = [lm, bm, im].filter(Boolean).sort((a, b) => a.index - b.index);
    if (candidates.length === 0) { push(rest); break; }
    const first = candidates[0];
    if (first.index > 0) push(rest.slice(0, first.index));
    if (first === lm) push(<a key={parts.length} href={first[2]} className="text-gold hover:underline">{first[1]}</a>);
    else if (first === bm) push(<strong key={parts.length} className="text-white font-semibold">{first[1]}</strong>);
    else push(<em key={parts.length} className="italic">{first[1]}</em>);
    rest = rest.slice(first.index + first[0].length);
  }
  return parts;
}
